import { CalendarDays, Clock, User } from 'lucide-react';
import type { AdditionalTask } from '@pms/shared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { fullName } from './taskConstants';

interface AdditionalWorkDetailModalProps {
  task: AdditionalTask | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

function fmtTime(t: string): string {
  return t.slice(0, 5);
}

function minutesToLabel(mins: number): string {
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground">{value}</p>
    </div>
  );
}

export function AdditionalWorkDetailModal({ task, open, onOpenChange }: AdditionalWorkDetailModalProps) {
  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-lg overflow-hidden">
        <DialogHeader>
          <DialogTitle className="break-words pr-6 text-lg font-bold leading-snug">{task.title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 overflow-hidden">
          {/* Date + Duration */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-2">
              <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <Field label="Work Date" value={fmtDate(task.work_date)} />
            </div>
            <div className="flex items-start gap-2">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
              <Field label="Duration" value={
                <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                  {minutesToLabel(task.duration_minutes)}
                </span>
              } />
            </div>
          </div>

          {/* Start + End time */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Start Time" value={fmtTime(task.start_time)} />
            <Field label="End Time" value={fmtTime(task.end_time)} />
          </div>

          {/* Description */}
          {task.description && (
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Description</p>
              <p className="break-words whitespace-pre-wrap text-sm text-foreground">{task.description}</p>
            </div>
          )}

          {/* Employee + Assigned By */}
          <div className="grid grid-cols-1 gap-3 rounded-lg border border-border/60 p-3 sm:grid-cols-2">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Employee</p>
                <p className="text-sm font-medium text-foreground">{fullName(task.first_name, task.last_name)}</p>
                {task.employee_code && (
                  <p className="text-xs text-muted-foreground">{task.employee_code}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Assigned By</p>
                <p className="text-sm text-foreground">
                  {task.assigned_by_first_name
                    ? fullName(task.assigned_by_first_name, task.assigned_by_last_name)
                    : '—'}
                </p>
              </div>
            </div>
          </div>

          {/* Created At */}
          <p className="text-right text-xs text-muted-foreground">
            Added {fmtDate(task.created_at)}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
